import { useEffect } from 'react';

/**
 * Manages Tab key cycling between major focus regions:
 * 1. TopBar (header)
 * 2. Left sidebar (tools)
 * 3. Canvas (blocks)
 * 4. Right sidebar (properties)
 *
 * Tab moves forward through regions, Shift+Tab moves backward.
 * Within each region, normal Tab behavior applies.
 */
const REGION_SELECTORS = [
  '[role="banner"]',           // TopBar
  '[data-focus-region="sidebar-left"]',
  '[role="application"]',      // Canvas
  '[data-focus-region="sidebar-right"]',
] as const;

function getFocusableElements(container: Element): HTMLElement[] {
  const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    el => !el.closest('[aria-hidden="true"]') && el.offsetParent !== null
  );
}

function getRegionElement(index: number): Element | null {
  return document.querySelector(REGION_SELECTORS[index]) ?? null;
}

export function useFocusTrap() {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const active = document.activeElement as HTMLElement | null;
      if (!active) return;

      // Determine which region the current focus is in
      let currentRegionIndex = -1;
      for (let i = 0; i < REGION_SELECTORS.length; i++) {
        const region = getRegionElement(i);
        if (region && region.contains(active)) {
          currentRegionIndex = i;
          break;
        }
      }

      if (currentRegionIndex === -1) return; // Not in a tracked region

      const region = getRegionElement(currentRegionIndex);
      if (!region) return;

      const focusable = getFocusableElements(region);
      if (focusable.length === 0) return;

      const isFirst = active === focusable[0];
      const isLast = active === focusable[focusable.length - 1];

      // Only intercept when Tab would leave the region
      if (e.shiftKey && isFirst) {
        // Move to previous region
        e.preventDefault();
        const prevIndex = (currentRegionIndex - 1 + REGION_SELECTORS.length) % REGION_SELECTORS.length;
        focusRegionEnd(prevIndex);
      } else if (!e.shiftKey && isLast) {
        // Move to next region
        e.preventDefault();
        const nextIndex = (currentRegionIndex + 1) % REGION_SELECTORS.length;
        focusRegionStart(nextIndex);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
}

function focusRegionStart(index: number) {
  for (let i = 0; i < REGION_SELECTORS.length; i++) {
    const regionIndex = (index + i) % REGION_SELECTORS.length;
    const region = getRegionElement(regionIndex);
    if (!region) continue;
    const focusable = getFocusableElements(region);
    if (focusable.length > 0) {
      focusable[0].focus();
      return;
    }
  }
}

function focusRegionEnd(index: number) {
  for (let i = 0; i < REGION_SELECTORS.length; i++) {
    const regionIndex = (index - i + REGION_SELECTORS.length) % REGION_SELECTORS.length;
    const region = getRegionElement(regionIndex);
    if (!region) continue;
    const focusable = getFocusableElements(region);
    if (focusable.length > 0) {
      focusable[focusable.length - 1].focus();
      return;
    }
  }
}
